import React, { useState } from 'react';
import { X, Mail, Phone, User, Search, Calendar, SlidersHorizontal } from 'lucide-react';
import BuyerPreferencesModal from '../../contacts/components/BuyerPreferencesModal';

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

const DeactivatedBuyersModal = ({ isOpen, onClose, buyers = [], rangeApplied = false, onBuyerClick }) => {
    const [searchTerm, setSearchTerm] = useState('');
    // Buyer whose buying preferences are being edited (null = closed).
    const [prefsIdentity, setPrefsIdentity] = useState(null);

    if (!isOpen) return null;

    const filtered = buyers.filter((b) => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
            b.name?.toLowerCase().includes(s) ||
            b.email?.toLowerCase().includes(s) ||
            b.phone?.toLowerCase().includes(s)
        );
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Deactivated buyer accounts</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs font-bold uppercase tracking-wider">
                                {rangeApplied ? 'In selected range' : 'All time'}
                            </span>
                            <span className="ml-2 text-gray-500">
                                ({filtered.length} {filtered.length === 1 ? 'account' : 'accounts'})
                            </span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                <div className="px-6 pt-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {filtered.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            {buyers.length === 0
                                ? 'No deactivated accounts in this date range.'
                                : 'No accounts match your search.'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buyer</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deactivated</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filtered.map((b) => {
                                        const clickable = !!onBuyerClick && !!b.id;
                                        const onRowClick = () => {
                                            if (!clickable) return;
                                            onBuyerClick({
                                                buyer_id: b.id,
                                                buyer_name: b.name,
                                                buyer_email: b.email,
                                            });
                                        };
                                        return (
                                            <tr
                                                key={b.id}
                                                className={`transition-colors ${clickable ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
                                                onClick={onRowClick}
                                            >
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-9 w-9 bg-red-50 rounded-full flex items-center justify-center">
                                                            <User className="w-4 h-4 text-red-600" />
                                                        </div>
                                                        <div className="ml-3">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {b.name || b.email || '—'}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPrefsIdentity({
                                                                        buyerId: b.id,
                                                                        email: b.email,
                                                                        name: b.name,
                                                                        phone: b.phone,
                                                                    });
                                                                }}
                                                                title="Edit this buyer's buying preferences"
                                                                className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-teal-300 hover:text-teal-600"
                                                            >
                                                                <SlidersHorizontal className="h-3.5 w-3.5" /> Preferences
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                    {b.email && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                            {b.email}
                                                        </div>
                                                    )}
                                                    {b.phone && (
                                                        <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-0.5">
                                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                            {b.phone}
                                                        </div>
                                                    )}
                                                    {!b.email && !b.phone && (
                                                        <span className="text-gray-400 text-xs">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                    <div className="flex items-center gap-1.5">
                                                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                                        {formatDate(b.deactivated_at)}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
            {prefsIdentity && (
                <BuyerPreferencesModal
                    identity={prefsIdentity}
                    title={prefsIdentity.name}
                    onClose={() => setPrefsIdentity(null)}
                />
            )}
        </div>
    );
};

export default DeactivatedBuyersModal;
